import * as mediasoupClient from 'mediasoup-client';
import { Device, Transport, Producer, Consumer } from 'mediasoup-client/lib/types';
import protooClient from 'protoo-client';
import { RootState, AppDispatch } from 'store';
import { fetchLiveSessionSettings, updateLiveStreamSettings } from 'store/reducers/livestreamSettings';
import { getProtooUrl } from './urlFactory';
import { AnyAction, Store } from 'redux';

const VIDEO_CONSTRAINS = {
    vga: {
        width: { ideal: 640 },
        height: { ideal: 480 },
    },
    hd: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
    },
};

const PC_PROPRIETARY_CONSTRAINTS = {
    optional: [{ googDscp: true }],
};

const VIDEO_KSVC_ENCODINGS = [{ scalabilityMode: "S3T3_KEY" }];

const VIDEO_SIMULCAST_ENCODINGS = [
    { scaleResolutionDownBy: 4 },
    { scaleResolutionDownBy: 2 },
    { scaleResolutionDownBy: 1 },
];
const VIDEO_SVC_ENCODINGS = [
    { scalabilityMode: 'S3T3', maxBitrate: 5000000 }
];

export default class RoomClient {
    private _closed: boolean = false;
    private _displayName: string;
    private _device: string;
    private _forceTcp: boolean;
    private _consume: boolean;
    private _useSimulcast: boolean;
    private _forceH264: boolean = false;
    private _forceVP9: boolean = false;
    private _useSharingSimulcast: boolean = true;
    private _protooUrl: string;
    private _protoo: protooClient.Peer | null = null;
    private _mediasoupDevice: Device | null = null;
    private _sendTransport: Transport | null = null;
    private _recvTransport: Transport | null = null;
    private _micProducer: Producer | null = null;
    private _webcamProducer: Producer | null = null;
    private _consumers: Map<string, Consumer> = new Map();
    private _webcams: Map<string, MediaDeviceInfo> = new Map();
    private _webcam: { device: MediaDeviceInfo | null; resolution: 'vga' | 'hd' };
    private _frameRate: { ideal: number; max: number };
    private _dispatch: AppDispatch;
    private _roomId: string;
    private _audioTrack: MediaStreamTrack | null = null;
    private _webcamStream: MediaStream | null = null;
    public stream: MediaStream;
    public _store: any;
    private _shareProducer: Producer | null = null;
    private _shareStream: MediaStream | null = null;
    private _strUserId: string;
    private _useDataChannel: boolean;
    private _dataConsumers: any;
    private _nextDataChannelTestNumber: number;
    private _botDataProducer: any;
    private _chatDataProducer: any;
    private _produce: boolean;
    private _bgImage: string | null;

    constructor({
        roomId,
        peerId,
        displayName,
        device = 'web',
        useSimulcast = false,
        forceTcp = false,
        consume = true,
        frameRate = { ideal: 15, max: 30 },
        resolution = 'vga',
        dispatch,
        store,
        userId,
        datachannel,
        produce,
        bgImage
    }: {
        roomId: string;
        peerId: string;
        displayName: string;
        device?: string;
        useSimulcast?: boolean;
        forceTcp?: boolean;
        consume?: boolean;
        frameRate?: { ideal: number; max: number };
        resolution?: 'vga' | 'hd';
        dispatch: AppDispatch;
        store: Store<RootState, AnyAction>;
        userId: string,
        datachannel: boolean;
        produce: boolean,
        bgImage: string | null
    }) {
        this._displayName = displayName;
        this._device = device;
        this._forceTcp = forceTcp;
        this._consume = consume;
        this._useSimulcast = useSimulcast;
        this._protooUrl = getProtooUrl({ roomId, peerId });
        this._webcam = { device: null, resolution };
        this._frameRate = frameRate;
        this._dispatch = dispatch;
        this._roomId = roomId;
        this._store = store;
        this._strUserId = userId;
        this._useDataChannel = datachannel;
        this._dataConsumers = new Map();
        this._nextDataChannelTestNumber = 0;
        this._botDataProducer = null;
        this._chatDataProducer = null;
        this._produce = produce;
        this._bgImage = bgImage;
    }

    async enableChatDataProducer() {
        console.debug('enableChatDataProducer()');

        if (!this._useDataChannel) return;

        // NOTE: Should enable this code but it's useful for testing.
        // if (this._chatDataProducer)
        // 	return;

        try {
            // Create chat DataProducer.
            this._chatDataProducer = await this._sendTransport.produceData({
                ordered: false,
                maxRetransmits: 1,
                label: 'chat',
                appData: { info: 'my-chat-DataProducer' },
            });

            // store.dispatch(
            //     stateActions.addDataProducer({
            //         id: this._chatDataProducer.id,
            //         sctpStreamParameters: this._chatDataProducer.sctpStreamParameters,
            //         label: this._chatDataProducer.label,
            //         protocol: this._chatDataProducer.protocol,
            //     }),
            // );

            this._chatDataProducer.on('transportclose', () => {
                this._chatDataProducer = null;
            });

            this._chatDataProducer.on('open', () => {
                console.debug('chat DataProducer "open" event');
            });

            this._chatDataProducer.on('close', () => {
                console.error('chat DataProducer "close" event');

                this._chatDataProducer = null;

                // store.dispatch(
                //     requestActions.notify({
                //         type: 'error',
                //         text: 'Chat DataProducer closed',
                //     }),
                // );
            });

            this._chatDataProducer.on('error', error => {
                console.error('chat DataProducer "error" event:%o', error);

                // store.dispatch(
                //     requestActions.notify({
                //         type: 'error',
                //         text: `Chat DataProducer error: ${error}`,
                //     }),
                // );
            });

            this._chatDataProducer.on('bufferedamountlow', () => {
                console.debug('chat DataProducer "bufferedamountlow" event');
            });
        } catch (error) {
            console.error('enableChatDataProducer() | failed:%o', error);

            // store.dispatch(
            //     requestActions.notify({
            //         type: 'error',
            //         text: `Error enabling chat DataProducer: ${error}`,
            //     }),
            // );

            throw error;
        }
    }

    async enableBotDataProducer() {
        console.debug('enableBotDataProducer()');

        if (!this._useDataChannel) return;

        // NOTE: Should enable this code but it's useful for testing.
        // if (this._botDataProducer)
        // 	return;

        try {
            // Create chat DataProducer.
            this._botDataProducer = await this._sendTransport.produceData({
                ordered: false,
                maxPacketLifeTime: 2000,
                label: 'bot',
                appData: { info: 'my-bot-DataProducer' },
            });

            // store.dispatch(
            //     stateActions.addDataProducer({
            //         id: this._botDataProducer.id,
            //         sctpStreamParameters: this._botDataProducer.sctpStreamParameters,
            //         label: this._botDataProducer.label,
            //         protocol: this._botDataProducer.protocol,
            //     }),
            // );

            this._botDataProducer.on('transportclose', () => {
                this._botDataProducer = null;
            });

            this._botDataProducer.on('open', () => {
                console.debug('bot DataProducer "open" event');
            });

            this._botDataProducer.on('close', () => {
                console.error('bot DataProducer "close" event');

                this._botDataProducer = null;

                // store.dispatch(
                //     requestActions.notify({
                //         type: 'error',
                //         text: 'Bot DataProducer closed',
                //     }),
                // );
            });

            this._botDataProducer.on('error', error => {
                console.error('bot DataProducer "error" event:%o', error);

                // store.dispatch(
                //     requestActions.notify({
                //         type: 'error',
                //         text: `Bot DataProducer error: ${error}`,
                //     }),
                // );
            });

            this._botDataProducer.on('bufferedamountlow', () => {
                console.debug('bot DataProducer "bufferedamountlow" event');
            });
        } catch (error) {
            console.error('enableBotDataProducer() | failed:%o', error);

            // store.dispatch(
            //     requestActions.notify({
            //         type: 'error',
            //         text: `Error enabling bot DataProducer: ${error}`,
            //     }),
            // );

            throw error;
        }
    }

    async join(autoOnMic: boolean = false, autoOnCam: boolean = false) {
        const protooTransport = new protooClient.WebSocketTransport(this._protooUrl);
        this._protoo = new protooClient.Peer(protooTransport);

        this._dispatch(updateLiveStreamSettings({ isLoading: true, roomState: "connecting" }));

        const reconnect = (attempt = 1) => {
            const delay = Math.min(30000, 2 ** attempt * 1000);
            setTimeout(() => {
                try {
                    this._consumers.forEach((consumer) => consumer.close());
                    this._consumers.clear();
                    const currentState = this._store.getState();
                    this._dispatch(updateLiveStreamSettings({ consumers: {}, peersNew: {} }));
                    this.join(autoOnMic, autoOnCam);
                } catch {
                    reconnect(attempt + 1);
                }
            }, delay + Math.random() * 100);
        };

        this._protoo.on('open', async () => {
            this._joinRoom(autoOnMic, autoOnCam);
        });

        this._protoo.on('failed', () => {
            console.warn('WebSocket connection failed');
            // reconnect();
        });

        this._protoo.on('disconnected', () => {
            if (this._sendTransport) {
                this._sendTransport.close();
                this._sendTransport = null;
            }
            if (this._recvTransport) {
                this._recvTransport.close();
                this._recvTransport = null;
            }
            this._dispatch(updateLiveStreamSettings({ isLoading: false }));
            reconnect();
        });

        this._protoo.on('close', () => {
            if (this._closed) return;
            this.close();
        });

        this._protoo.on('request', async (request, accept, reject) => {
            switch (request.method) {
                case 'pong': {
                    await this._protoo.request('ping');
                    accept();
                    break;
                }
                case 'newConsumer': {
                    if (!this._consume) {
                        reject(403, 'I do not want to consume');
                        break;
                    }
                    const { peerId, producerId, id, kind, rtpParameters, type, appData, producerPaused } = request.data;

                    try {
                        const consumer = await this._recvTransport.consume({
                            id,
                            producerId,
                            kind,
                            rtpParameters,
                            appData: { ...appData, peerId },
                        });

                        const currentState = this._store.getState();
                        const currentConsumers = currentState.live.settings?.consumers || {};
                        const existingConsumer: any = Object.values(currentConsumers).find(
                            (c: any) => c.peerId === peerId && c.kind === kind && c.producerId === producerId && c.id !== id
                        );

                        if (existingConsumer) {
                            // Close the existing consumer
                            const oldConsumer = this._consumers.get(existingConsumer.id);
                            if (oldConsumer) {
                                oldConsumer.close();
                                this._consumers.delete(existingConsumer.id);
                            }
                            // Remove from Redux state
                            const updatedConsumers = { ...currentConsumers };
                            delete updatedConsumers[existingConsumer.id];
                            this._dispatch(updateLiveStreamSettings({ consumers: updatedConsumers }));
                            this._dispatch(fetchLiveSessionSettings(this._roomId));
                        }

                        this._consumers.set(consumer.id, consumer);

                        consumer.on('transportclose', () => {
                            this._consumers.delete(consumer.id);
                        });

                        const { spatialLayers, temporalLayers } = mediasoupClient.parseScalabilityMode(
                            consumer.rtpParameters.encodings[0].scalabilityMode || 'L1T3'
                        );

                        // Remove this line to avoid updating local audio/video state
                        // this._dispatch(updateLiveStreamSettings({
                        //     [kind === 'audio' ? 'audio' : 'video']: !producerPaused,
                        // }));

                        this._dispatch(updateLiveStreamSettings({
                            consumers: {
                                ...currentConsumers,
                                [consumer.id]: {
                                    id: consumer.id,
                                    peerId,
                                    kind,
                                    type,
                                    locallyPaused: false,
                                    remotelyPaused: producerPaused,
                                    rtpParameters: consumer.rtpParameters,
                                    spatialLayers,
                                    temporalLayers,
                                    preferredSpatialLayer: spatialLayers - 1,
                                    preferredTemporalLayer: temporalLayers - 1,
                                    priority: 1,
                                    codec: consumer.rtpParameters.codecs[0].mimeType.split('/')[1],
                                    track: consumer.track,
                                    producerId,
                                    appData
                                },
                            },
                            consumerPeers: this.getConsumerPeers({ ...currentConsumers},consumer),
                        }));
                        accept();
                    } catch (error) {
                        console.error('Error creating consumer:', error);
                        reject(500, 'Error creating consumer');
                    }
                    break;
                }
                case 'closeProducer': {
                    const { producerId } = request.data;
                    if (this._shareProducer && producerId === this._shareProducer.id) {
                        await this.disableShare();
                        accept();
                    } else {
                        reject(404, 'Producer not found');
                    }
                    break;
                }
                case 'newDataConsumer': {
                    if (!this._consume) {
                        reject(403, 'I do not want to data consume');

                        break;
                    }

                    if (!this._useDataChannel) {
                        reject(403, 'I do not want DataChannels');

                        break;
                    }

                    const {
                        peerId, // NOTE: Null if bot.
                        dataProducerId,
                        id,
                        sctpStreamParameters,
                        label,
                        protocol,
                        appData,
                    } = request.data;

                    try {
                        const dataConsumer = await this._recvTransport.consumeData({
                            id,
                            dataProducerId,
                            sctpStreamParameters,
                            label,
                            protocol,
                            appData: { ...appData, peerId }, // Trick.
                        });

                        // Store in the map.
                        this._dataConsumers.set(dataConsumer.id, dataConsumer);

                        dataConsumer.on('transportclose', () => {
                            this._dataConsumers.delete(dataConsumer.id);
                        });

                        dataConsumer.on('open', () => {
                            console.debug('DataConsumer "open" event');
                        });

                        dataConsumer.on('close', () => {
                            console.warn('DataConsumer "close" event');

                            this._dataConsumers.delete(dataConsumer.id);

                            // store.dispatch(
                            //     requestActions.notify({
                            //         type: 'error',
                            //         text: 'DataConsumer closed',
                            //     }),
                            // );
                        });

                        dataConsumer.on('error', error => {
                            console.error('DataConsumer "error" event:%o', error);

                            // store.dispatch(
                            //     requestActions.notify({
                            //         type: 'error',
                            //         text: `DataConsumer error: ${error}`,
                            //     }),
                            // );
                        });

                        dataConsumer.on('message', message => {
                            console.log(
                                'DataConsumer "message" event [streamId:%d]',
                                dataConsumer.sctpStreamParameters.streamId,
                            );

                            // TODO: For debugging.
                            // window.DC_MESSAGE = message;

                            if (message instanceof ArrayBuffer) {
                                const view = new DataView(message);
                                const number = view?.getUint32(0);

                                if (number == Math.pow(2, 32) - 1) {
                                    console.warn('dataChannelTest finished!');

                                    this._nextDataChannelTestNumber = 0;

                                    return;
                                }

                                if (number > this._nextDataChannelTestNumber) {
                                    console.warn(
                                        'dataChannelTest: %s packets missing',
                                        number - this._nextDataChannelTestNumber,
                                    );
                                }

                                this._nextDataChannelTestNumber = number + 1;

                                return;
                            } else if (typeof message !== 'string') {
                                console.warn('ignoring DataConsumer "message" (not a string)');

                                return;
                            }

                            switch (dataConsumer.label) {
                                case 'chat': {
                                    const { peers } = this._store.getState();
                                    const peersArray = Object.keys(peers).map(
                                        _peerId => peers[_peerId],
                                    );
                                    const sendingPeer = peersArray.find(peer =>
                                        peer.dataConsumers.includes(dataConsumer.id),
                                    );

                                    if (!sendingPeer) {
                                        console.warn('DataConsumer "message" from unknown peer');

                                        break;
                                    }

                                    // store.dispatch(
                                    //     requestActions.notify({
                                    //         title: `${sendingPeer.displayName} says:`,
                                    //         text: message,
                                    //         timeout: 5000,
                                    //     }),
                                    // );

                                    break;
                                }

                                case 'bot': {
                                    // store.dispatch(
                                    //     requestActions.notify({
                                    //         title: 'Message from Bot:',
                                    //         text: message,
                                    //         timeout: 5000,
                                    //     }),
                                    // );

                                    break;
                                }
                            }
                        });

                        // TODO: REMOVE
                        // window.DC = dataConsumer;

                        // store.dispatch(
                        //     stateActions.addDataConsumer(
                        //         {
                        //             id: dataConsumer.id,
                        //             sctpStreamParameters: dataConsumer.sctpStreamParameters,
                        //             label: dataConsumer.label,
                        //             protocol: dataConsumer.protocol,
                        //         },
                        //         peerId,
                        //     ),
                        // );

                        // We are ready. Answer the protoo request.
                        accept();
                    } catch (error) {

                        throw error;
                    }

                    break;
                }
            }
        });

        this._protoo.on('notification', (notification) => {
            switch (notification.method) {

                case 'consumerClosed': {
                    const { consumerId } = notification.data;
                    const consumer = this._consumers.get(consumerId);
                    if (!consumer) return;
                    consumer.close();
                    this._consumers.delete(consumerId);
                    const currentState = this._store.getState();
                    const consumers = { ...currentState.live.settings.consumers };
                    delete consumers[consumerId];
                    this._dispatch(updateLiveStreamSettings({ consumers }));
                    break;
                }
                case 'newPeer': {
                    const peer = notification.data;
                    const currentState = this._store.getState();
                    this._dispatch(updateLiveStreamSettings({
                        peersNew: {
                            ...currentState.live.settings.peersNew,
                            [peer.id]: {
                                id: peer.id,
                                displayName: peer.displayName,
                                device: peer.device,
                                consumers: [],
                            },
                        },
                    }));
                    break;
                }
                case 'peerClosed': {
                    const { peerId } = notification.data;
                    // Remove all consumers for this peer
                    const currentState = this._store.getState();
                    const currentConsumers = { ...currentState.live.settings.consumers };
                    Object.keys(currentConsumers).forEach((consumerId) => {
                        if (currentConsumers[consumerId].peerId === peerId) {
                            const consumer = this._consumers.get(consumerId);
                            if (consumer) {
                                consumer.close();
                                this._consumers.delete(consumerId);
                            }
                            delete currentConsumers[consumerId];
                        }
                    });

                    // Remove peer from peersNew
                    const peersNew = { ...currentState.live.settings.peersNew };
                    delete peersNew[peerId];

                    this._dispatch(updateLiveStreamSettings({ consumers: currentConsumers, peersNew }));
                    break;
                }
                case 'consumerClosed': {
                    const { consumerId } = notification.data;
                    const consumer = this._consumers.get(consumerId);
                    if (!consumer) return;
                    consumer.close();
                    this._consumers.delete(consumerId);
                    const currentState = this._store.getState();
                    const consumers = { ...currentState.live.settings.consumers };
                    delete consumers[consumerId];
                    this._dispatch(updateLiveStreamSettings({ consumers }));
                    break;
                }
                case 'consumerPaused': {
                    const { consumerId } = notification.data;
                    const consumer = this._consumers.get(consumerId);
                    if (!consumer) return;
                    const consumers = {
                        ...this._store.getState().live.settings.consumers,
                        [consumerId]: {
                            ...this._store.getState().live.settings.consumers[consumerId],
                            remotelyPaused: true,
                        },
                    };
                    this._dispatch(updateLiveStreamSettings({ consumers }));
                    break;
                }
                case 'consumerResumed': {
                    const { consumerId } = notification.data;
                    const consumer = this._consumers.get(consumerId);
                    if (!consumer) return;

                    const consumers = {
                        ...this._store.getState().live.settings.consumers,
                        [consumerId]: {
                            ...this._store.getState().live.settings.consumers[consumerId],
                            remotelyPaused: false,
                        },
                    };
                    this._dispatch(updateLiveStreamSettings({ consumers }));
                    break;
                }
                case 'peerDisplayNameChanged': {
                    const { peerId, displayName } = notification.data;
                    const peersNew = {
                        ...this._store.getState().live.settings.peersNew,
                        [peerId]: {
                            ...this._store.getState().live.settings.peersNew[peerId],
                            displayName,
                        },
                    };
                    this._dispatch(updateLiveStreamSettings({ peersNew }));
                    break;
                }

            }
        });
    }

    async close() {
        if (this._closed) return;

        this._closed = true;

        // Close protoo Peer
        this._protoo.close();

        // Close mediasoup Transports.
        if (this._sendTransport) this._sendTransport.close();

        if (this._recvTransport) this._recvTransport.close();
        this._dispatch(updateLiveStreamSettings({
            roomState: "closed",
            // isMicAccessGranted: false,
            // isCameraAccessGranted: false,
            video: true,
            audio: true,
            producers: {},
            consumers: {},
            // mode: 'Break/Networking Mode',
        }));
    }

    async enableMic() {

        if (this._micProducer) return;

        if (!this._mediasoupDevice) {
            console.warn('enableMic: mediasoup device is not initialized');
            return;
        }

        if (!this._mediasoupDevice.loaded) {
            console.warn('enableMic: device not loaded yet, skipping mic enable');
            return;
        }

        if (!this._mediasoupDevice.canProduce("audio")) {
            console.warn("enableMic: cannot produce audio");
            return;
        }

        try {
            let audioTrack: MediaStreamTrack | null = null;
            if (this.stream && this.stream.getAudioTracks().length > 0) {
                audioTrack = this.stream.getAudioTracks()[0];
            } else {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    },
                });
                audioTrack = stream.getAudioTracks()[0];
                this.stream = stream; // Optionally update the stream property
            }
            this._audioTrack = audioTrack;

            if (!this._sendTransport) {
                throw new Error('Send transport not ready');
            }

            this._micProducer = await this._sendTransport.produce({
                track: this._audioTrack,
                codecOptions: { opusStereo: true, opusDtx: true },
            });

            const producerData = {
                id: this._micProducer.id,
                kind: 'audio',
                track: audioTrack,
                paused: this._micProducer.paused,
                rtpParameters: this._micProducer.rtpParameters,
                codec: this._micProducer.rtpParameters.codecs[0].mimeType.split('/')[1]
            };

            this._dispatch(updateLiveStreamSettings({
                // audio: true,
                producers: {
                    ...this._store.getState().live.settings.producers,
                    [this._micProducer.id]: producerData
                }
            }));
            this._micProducer.on('transportclose', () => {
                this._micProducer = null;
                this._dispatch(updateLiveStreamSettings({
                    producers: Object.fromEntries(
                        Object.entries(this._store.getState().live.settings.producers).filter(
                            ([key]) => key !== this._micProducer?.id
                        )
                    )
                }));
            });

            this._micProducer.on('trackended', () => {
                this.disableMic();
            });
        } catch (error) {
            console.error('Error enabling mic:', error);
            this._dispatch(updateLiveStreamSettings({
                producers: Object.fromEntries(
                    Object.entries(this._store.getState().live.settings.producers).filter(
                        ([key]) => key !== this._micProducer?.id
                    )
                )
            }));
            if (this._audioTrack) {
                this._audioTrack.stop();
                this._audioTrack = null;
            }
        }
    }

    async disableMic() {
        if (!this._micProducer) return;
        const producerId = this._micProducer.id;
        this._micProducer.close();
        try {
            await this._protoo?.request('closeProducer', { producerId: this._micProducer.id });
        } catch (error) {
            console.error('Error closing mic producer:', error);
        }
        this._micProducer = null;
        if (this._audioTrack) {
            this._audioTrack.stop();
            this._audioTrack = null;
        }
        this._dispatch(updateLiveStreamSettings({
            producers: Object.fromEntries(
                Object.entries(this._store.getState().live.settings.producers).filter(
                    ([key]) => key !== producerId
                )
            )
        }));
    }

    async muteMic() {
        try {
            this._micProducer.pause();
            await this._protoo.request('pauseProducer', { producerId: this._micProducer.id });
            this._dispatch(updateLiveStreamSettings({
                producers: {
                    ...this._store.getState().live.settings.producers,
                    [this._micProducer.id]: {
                        ...this._store.getState().live.settings.producers[this._micProducer.id],
                        paused: true
                    }
                }
            }));
        } catch (error) {
            console.error('Error muting mic:', error);
        }
    }

    async unmuteMic() {
        if (!this._micProducer) return;
        try {
            this._micProducer.resume();
            await this._protoo?.request('resumeProducer', { producerId: this._micProducer.id });
            this._dispatch(updateLiveStreamSettings({
                producers: {
                    ...this._store.getState().live.settings.producers,
                    [this._micProducer.id]: {
                        ...this._store.getState().live.settings.producers[this._micProducer.id],
                        paused: false
                    }
                }
            }));
        } catch (error) {
            console.error('Error unmuting mic:', error);
        }
    }

    startProducing() {
        if (this._sendTransport) {
            return Promise.resolve();
        }

        return this._protoo
            .request("createWebRtcTransport", {
                forceTcp: this._forceTcp,
                producing: true,
                consuming: false,
            })
            .then((transportInfo) => {
                const { id, iceParameters, iceCandidates, dtlsParameters } =
                    transportInfo;

                this._sendTransport = this._mediasoupDevice.createSendTransport({
                    id,
                    iceParameters,
                    iceCandidates,
                    dtlsParameters,
                });

                this._sendTransport.on(
                    "connect",
                    ({ dtlsParameters }, callback, errback) => {
                        this._protoo
                            .request("connectWebRtcTransport", {
                                transportId: this._sendTransport.id,
                                dtlsParameters,
                            })
                            .then(callback)
                            .catch(errback);
                    }
                );

                this._sendTransport.on(
                    "produce",
                    async ({ kind, rtpParameters, appData }, callback, errback) => {
                        try {
                            const { id } = await this._protoo.request("produce", {
                                transportId: this._sendTransport.id,
                                kind,
                                rtpParameters,
                                appData,
                            });

                            // this.changeOnlyConsumer(false);
                            callback({ id });
                            // промис будет pending
                        } catch (error) {
                            errback(error);
                        }
                    }
                );

                // store.dispatch(this.setAction().setProduce(true));

                const payload = {
                    canSendMic: this._mediasoupDevice.canProduce("audio"),
                    canSendWebcam: this._mediasoupDevice.canProduce("video"),
                };

                // store.dispatch(this.setAction().setMediaCapabilities(payload));
            })
            .catch((err) => {
            });
    }

    checkWebCam() {
        const dispatchDisableAction = (webCamState, settings) => {
            const payload = { video: webCamState };
            this._store.dispatch(updateLiveStreamSettings(settings));
        };

        navigator.mediaDevices
            .getUserMedia({ video: true })
            .then((stream) => stream?.getTracks()?.forEach((track) => track?.stop()))
            .then(() => {
                const { video } = this._store.getState().live.settings;

                if (video) {
                    const payload = { video: true };
                    dispatchDisableAction(true, payload);
                }
            })
            .catch((err) => {
                if (err) {
                    if (err.name === "NotAllowedError") {
                        const payload = { isCamerAccessGranted: false };
                        dispatchDisableAction(false, payload);

                    } else if (err.name === "NotFoundError") {
                        const payload = { video: false };
                        dispatchDisableAction(false, payload);

                    }
                }
            });
    }

    async enableWebcam() {
        if (this._webcamProducer) {
            return;
        }

        if (!this._mediasoupDevice) {
            console.warn('enableWebcam: mediasoup device is not initialized');
            return;
        }

        if (!this._mediasoupDevice.loaded) {
            console.warn('enableWebcam: device not loaded yet, skipping mic enable');
            return;
        }

        if (!this._mediasoupDevice.canProduce("video")) {
            console.warn("enableWebcam: cannot produce video");
            return;
        }

        let track: MediaStreamTrack;
        try {
            await this._updateWebcams();
            const device = this._webcam.device;
            if (!device) {
                console.error('No webcam devices found');
                throw new Error('No webcam devices');
            }

            // Check if stream has valid video tracks
            if (this.stream instanceof MediaStream && this.stream.getVideoTracks().length > 0) {
                track = this.stream.getVideoTracks()[0];
                if (track.readyState !== 'live') {
                    console.warn('Video track is not live:', track);
                    track.stop();
                    track = null;
                } else {
                    this.stream = null;
                }
            }

            if (!track) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        deviceId: device.deviceId,
                        ...VIDEO_CONSTRAINS[this._webcam.resolution],
                        frameRate: this._frameRate,
                    },
                });
                track = stream.getVideoTracks()[0];
                this._webcamStream = stream;
                this.stream = stream;
            }

            if (!this._sendTransport) {
                throw new Error('Send transport not ready');
            }

            const encodings = this._useSimulcast ? VIDEO_SIMULCAST_ENCODINGS : [{ maxBitrate: 400000 }];
            if (this._useSimulcast) {
                // If VP9 is the only available video codec then use SVC.
                const firstVideoCodec =
                    this._mediasoupDevice.rtpCapabilities.codecs.find(
                        (c) => c.kind === "video"
                    );

                let encodings;

                if (firstVideoCodec.mimeType.toLowerCase() === "video/vp9") {
                    encodings = VIDEO_KSVC_ENCODINGS;
                } else {
                    encodings = VIDEO_SIMULCAST_ENCODINGS;
                }

                this._webcamProducer = await this._sendTransport.produce({
                    track,
                    encodings,
                    codecOptions: {
                        videoGoogleStartBitrate: 1000,
                    },
                });
            } else {
                this._webcamProducer = await this._sendTransport.produce({
                    track,
                    encodings: [
                        {
                            maxBitrate: 400000,
                        },
                    ],
                });
            }
            // this._webcamProducer = await this._sendTransport.produce({
            //     track,
            //     encodings,
            //     codecOptions: { videoGoogleStartBitrate: 1000 },
            // });

            const producerData = {
                id: this._webcamProducer.id,
                kind: 'video',
                track: track,
                paused: this._webcamProducer.paused,
                rtpParameters: this._webcamProducer.rtpParameters,
                codec: this._webcamProducer.rtpParameters.codecs[0].mimeType.split('/')[1]
            };


            this._dispatch(updateLiveStreamSettings({
                producers: {
                    ...this._store.getState().live.settings.producers,
                    [this._webcamProducer.id]: producerData
                }
            }));

            this._webcamProducer.on('transportclose', () => {
                this._webcamProducer = null;
                this._dispatch(updateLiveStreamSettings({
                    producers: Object.fromEntries(
                        Object.entries(this._store.getState().live.settings.producers).filter(
                            ([key]) => key !== this._webcamProducer?.id
                        )
                    )
                }));
            });

            this._webcamProducer.on('trackended', () => {
                this.disableWebcam();
            });
        } catch (error) {
            console.error('Error enabling webcam:', error);
            this._dispatch(updateLiveStreamSettings({
                // video: false,
                producers: Object.fromEntries(
                    Object.entries(this._store.getState().live.settings.producers).filter(
                        ([key]) => key !== this._webcamProducer?.id
                    )
                )
            }));
            if (track) {
                track.stop();
            }
        }
    }

    async disableWebcam() {
        if (!this._webcamProducer) return;

        const producerId = this._webcamProducer.id;
        this._webcamProducer.close();
        try {
            await this._protoo?.request('closeProducer', { producerId: this._webcamProducer.id });
        } catch (error) {
            console.error('Error closing webcam producer:', error);
        }
        this._webcamProducer = null;
        this._dispatch(updateLiveStreamSettings({
            producers: Object.fromEntries(
                Object.entries(this._store.getState().live.settings.producers).filter(
                    ([key]) => key !== producerId
                )
            )
        }));
        if (this._webcamStream) {
            this._webcamStream.getTracks().forEach(track => track.stop());
            this._webcamStream = null;
        }
    }

    // private async _joinRoom(autoOnMic: boolean, autoOnVideo: boolean) {
    //     try {
    //         this._mediasoupDevice = new mediasoupClient.Device();
    //         const routerRtpCapabilities = await this._protoo!.request('getRouterRtpCapabilities');
    //         await this._mediasoupDevice.load({ routerRtpCapabilities });

    //         if (this._consume) {
    //             const transportInfo = await this._protoo!.request('createWebRtcTransport', {
    //                 forceTcp: this._forceTcp,
    //                 producing: false,
    //                 consuming: true,
    //             });

    //             const { id, iceParameters, iceCandidates, dtlsParameters } = transportInfo;
    //             this._recvTransport = this._mediasoupDevice.createRecvTransport({
    //                 id,
    //                 iceParameters,
    //                 iceCandidates,
    //                 dtlsParameters,
    //             });

    //             this._recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
    //                 this._protoo!.request('connectWebRtcTransport', {
    //                     transportId: this._recvTransport!.id,
    //                     dtlsParameters,
    //                 })
    //                     .then(callback)
    //                     .catch(errback);
    //             });
    //         }

    //         const transportInfo = await this._protoo!.request('createWebRtcTransport', {
    //             forceTcp: this._forceTcp,
    //             producing: true,
    //             consuming: false,
    //         });

    //         const { id, iceParameters, iceCandidates, dtlsParameters } = transportInfo;
    //         this._sendTransport = this._mediasoupDevice.createSendTransport({
    //             id,
    //             iceParameters,
    //             iceCandidates,
    //             dtlsParameters,
    //         });

    //         this._sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
    //             this._protoo!.request('connectWebRtcTransport', {
    //                 transportId: this._sendTransport!.id,
    //                 dtlsParameters,
    //             })
    //                 .then(callback)
    //                 .catch(errback);
    //         });

    //         this._sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
    //             try {
    //                 const { id } = await this._protoo!.request('produce', {
    //                     transportId: this._sendTransport!.id,
    //                     kind,
    //                     rtpParameters,
    //                     appData,
    //                 });
    //                 callback({ id });
    //             } catch (error) {
    //                 errback(error);
    //             }
    //         });

    //         await this._protoo!.request('join', {
    //             displayName: this._displayName,
    //             device: this._device,
    //             rtpCapabilities: this._consume ? this._mediasoupDevice.rtpCapabilities : undefined,
    //         });

    //         this._dispatch(updateLiveStreamSettings({ isLoading: false }));

    //         // Only enable mic since no camera is available
    //         if (autoOnMic) {
    //             await this.enableMic();
    //             setTimeout(() => this.unmuteMic(), 100);
    //         }
    //         if (autoOnVideo) {
    //             await this.enableWebcam();
    //         }
    //     } catch (error) {
    //         console.error('Error joining room:', error);
    //         this._dispatch(updateLiveStreamSettings({ isLoading: false }));
    //         setTimeout(() => this._joinRoom(autoOnMic, autoOnVideo), 5000);
    //     }
    // }

    private async _joinRoom(autoOnMic: boolean, autoOnVideo: boolean) {
        try {
            this._mediasoupDevice = new mediasoupClient.Device();
            const routerRtpCapabilities = await this._protoo.request('getRouterRtpCapabilities');
            await this._mediasoupDevice.load({ routerRtpCapabilities });

            // Create send transport first
            try {
                const transportInfo = await this._protoo.request('createWebRtcTransport', {
                    forceTcp: this._forceTcp,
                    producing: true,
                    consuming: false,
                    sctpCapabilities: this._useDataChannel
                        ? this._mediasoupDevice.sctpCapabilities
                        : undefined,
                });

                console.log("transportInfo", transportInfo);


                const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters, } = transportInfo;
                this._sendTransport = this._mediasoupDevice.createSendTransport({
                    id,
                    iceParameters,
                    iceCandidates,
                    dtlsParameters: {
                        ...dtlsParameters,
                        role: 'auto',
                    },
                    // sctpParameters,
                    // iceServers: [],
                    // proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS,
                    // additionalSettings: {
                    //     encodedInsertableStreams: false,
                    // },
                });

                this._sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
                    this._protoo.request('connectWebRtcTransport', {
                        transportId: this._sendTransport.id,
                        dtlsParameters,
                    })
                        .then(callback)
                        .catch(errback);
                });

                this._sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
                    try {
                        const { id } = await this._protoo.request('produce', {
                            transportId: this._sendTransport.id,
                            kind,
                            rtpParameters,
                            appData,
                        });
                        callback({ id });
                    } catch (error) {
                        errback(error);
                    }
                });
                this._sendTransport.on(
                    'producedata',
                    async (
                        { sctpStreamParameters, label, protocol, appData },
                        callback,
                        errback,
                    ) => {

                        try {
                            // eslint-disable-next-line no-shadow
                            const { id } = await this._protoo.request('produceData', {
                                transportId: this._sendTransport.id,
                                sctpStreamParameters,
                                label,
                                protocol,
                                appData,
                            });

                            callback({ id });
                        } catch (error) {
                            errback(error);
                        }
                    },
                );
            } catch (error) {
                console.warn(`Send transport creation failed, retries left:`, error);
                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
            }

            // Create receive transport (if consuming)
            if (this._consume) {
                try {
                    const transportInfo = await this._protoo.request('createWebRtcTransport', {
                        forceTcp: this._forceTcp,
                        producing: false,
                        consuming: true,
                        sctpCapabilities: this._useDataChannel
                            ? this._mediasoupDevice.sctpCapabilities
                            : undefined,
                    });

                    const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters } = transportInfo;
                    this._recvTransport = this._mediasoupDevice.createRecvTransport({
                        id,
                        iceParameters,
                        iceCandidates,
                        dtlsParameters:
                        {
                            ...dtlsParameters,
                            role: 'auto',
                        },
                        sctpParameters,
                        iceServers: [],
                    });

                    this._recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
                        this._protoo.request('connectWebRtcTransport', {
                            transportId: this._recvTransport.id,
                            dtlsParameters,
                        })
                            .then(callback)
                            .catch(errback);
                    });
                } catch (error) {
                    console.warn(`Receive transport creation failed, retries left:`, error);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
                }
            }

            const { peers } = await this._protoo.request('join', {
                displayName: this._displayName,
                device: this._device,
                userId: this._strUserId,
                rtpCapabilities: this._consume ? this._mediasoupDevice.rtpCapabilities : undefined,
                sctpCapabilities:
                    this._useDataChannel && this._consume
                        ? this._mediasoupDevice.sctpCapabilities
                        : undefined,

                bgImage: this._bgImage,
            });

            // Dispatch existing peers to Redux store
            const peersMap = peers.reduce((acc: { [key: string]: any }, peer: any) => {
                acc[peer.id] = {
                    id: peer.id,
                    displayName: peer.displayName,
                    device: peer.device,
                    consumers: [],
                };
                return acc;
            }, {});
            if (peersMap) {
                this._dispatch(updateLiveStreamSettings({ peersNew: peersMap, isLoading: false, roomState: "connected" }));
            } else {
                this._dispatch(updateLiveStreamSettings({ peersNew: {}, isLoading: false, roomState: "" }));
            }
            // this.checkWebCam();

            // Only enable mic since no camera is available
            if (autoOnMic) {
                await this.enableMic();
            }
            if (autoOnVideo) {
                await this.enableWebcam();
            }
            // this._sendTransport.on('connectionstatechange', connectionState => {
            //     if (connectionState === 'connected') {
            //         this.enableChatDataProducer();
            //         this.enableBotDataProducer();
            //     }
            // });
        } catch (error) {
            // console.error('Error joining room:', error);
            this._dispatch(updateLiveStreamSettings({ isLoading: false }));
            setTimeout(() => this._joinRoom(autoOnMic, autoOnVideo), 5000);
        }
    }

    private async _updateWebcams() {
        this._webcams.clear();
        const devices = await navigator.mediaDevices.enumerateDevices();
        for (const device of devices) {
            if (device.kind === 'videoinput') {
                this._webcams.set(device.deviceId, device);
            }
        }
        const array = Array.from(this._webcams.values());
        this._webcam.device = array[0] || null;
    }

    // Expose method to check mic producer state
    isMicMuted() {
        return this._micProducer ? this._micProducer.paused : true;
    }

    getWebcamStream(): MediaStream | null {
        return this._webcamStream;
    }

    async enableShare() {
        // Prevent multiple screen-sharing attempts
        if (this._shareProducer || this._shareStream) {
            console.log('Screen sharing already in progress');
            return;
        }
        if (!navigator.mediaDevices.getDisplayMedia) {
            console.error('getDisplayMedia not supported');
            return;
        }
        if (!this._mediasoupDevice?.canProduce('video')) {
            console.error('enableShare: cannot produce video');
            return;
        }

        let track: MediaStreamTrack | undefined;
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                audio: true,
                video: true,
            });

            if (!stream) {
                throw new Error('Screen sharing stream was not received');
            }

            track = stream.getVideoTracks()[0];
            this._shareStream = stream;

            let encodings;
            let codec;
            const codecOptions = {
                videoGoogleStartBitrate: 1000,
            };

            if (this._forceH264) {
                codec = this._mediasoupDevice.rtpCapabilities.codecs.find(
                    (c) => c.mimeType.toLowerCase() === 'video/h264'
                );
                if (!codec) {
                    throw new Error('desired H264 codec+configuration is not supported');
                }
            } else if (this._forceVP9) {
                codec = this._mediasoupDevice.rtpCapabilities.codecs.find(
                    (c) => c.mimeType.toLowerCase() === 'video/vp9'
                );
                if (!codec) {
                    throw new Error('desired VP9 codec+configuration is not supported');
                }
            }

            if (this._useSharingSimulcast) {
                const firstVideoCodec = this._mediasoupDevice.rtpCapabilities.codecs.find(
                    (c) => c.kind === 'video'
                );
                if ((this._forceVP9 && codec) || firstVideoCodec?.mimeType.toLowerCase() === 'video/vp9') {
                    encodings = VIDEO_SVC_ENCODINGS;
                } else {
                    encodings = VIDEO_SIMULCAST_ENCODINGS.map((encoding) => ({
                        ...encoding,
                        dtx: true,
                    }));
                }
            }

            if (!this._sendTransport) {
                throw new Error('Transport not yet ready');
            }

            this._shareProducer = await this._sendTransport.produce({
                track,
                encodings,
                codecOptions,
                codec,
                appData: {
                    share: true,
                    color: 'red',
                    name: 'screenShare',
                },
            });

            // Dispatch state update only after successful producer creation
            this._dispatch(
                updateLiveStreamSettings({
                    isScreenPresented: true,
                    consumers: {
                        ...this._store.getState().live.settings.consumers,
                        [this._shareProducer.id]: {
                            id: this._shareProducer.id,
                            peerId: this._roomId,
                            kind: 'video',
                            type: 'share',
                            locallyPaused: false,
                            remotelyPaused: this._shareProducer.paused,
                            rtpParameters: this._shareProducer.rtpParameters,
                            spatialLayers: 3,
                            temporalLayers: 3,
                            preferredSpatialLayer: 2,
                            preferredTemporalLayer: 2,
                            priority: 1,
                            codec: this._shareProducer.rtpParameters.codecs[0].mimeType.split('/')[1],
                            track: this._shareProducer.track,
                            producerId: this._shareProducer.id,
                        },
                    },
                })
            );

            this._shareProducer.on('transportclose', () => {
                this._shareProducer = null;
                this._shareStream = null;
                this._dispatch(updateLiveStreamSettings({ isScreenPresented: false }));
            });

            this._shareProducer.on('trackended', () => {
                console.info('Share disconnected!');
                this.disableShare();
            });
        } catch (error) {
            console.error(`Error sharing: ${error}`);
            if (track) track.stop();
            this._shareStream = null;
            // Ensure state is reset on error
            this._dispatch(updateLiveStreamSettings({ isScreenPresented: false }));
        }
    }

    async disableShare() {
        if (!this._shareProducer) {
            return;
        }

        this._shareProducer.close();
        try {
            await this._protoo?.request('closeProducer', {
                producerId: this._shareProducer.id,
            });
        } catch (err) {
            console.error('Error closing server-side share Producer:', err);
        } finally {
            this._shareProducer = null;
            if (this._shareStream) {
                this._shareStream.getTracks().forEach((track) => track.stop());
                this._shareStream = null;
            }
            // Ensure state is updated
            this._dispatch(updateLiveStreamSettings({ isScreenPresented: false }));
        }
    }

    async changeBgImage(bgImage, attempt = 1) {
        console.log(`changing display image now (Attempt ${attempt})`);

        try {
            await this._protoo.request('changeBgImage', { bgImage });
            const authUser = this._store.getState().auth.user?.email;

            this._bgImage = bgImage;
            this._dispatch(updateLiveStreamSettings({ [authUser]: bgImage }));

            console.log('Background image updated successfully.');
        }
        catch (error) {
            console.error(`Error occurred while changing background image (Attempt ${attempt}):`, error);

            if (attempt < 3) {
                const retryDelay = 500 * attempt; // progressive delay: 500ms, 1000ms, 1500ms
                console.log(`Retrying changeBgImage in ${retryDelay}ms...`);

                setTimeout(() => {
                    this.changeBgImage(bgImage, attempt + 1);
                }, retryDelay);
            }
            else {
                console.error('All retry attempts failed for changeBgImage().');
            }
        }
    }


    async replaceVideoStream(canvasTrack, attempt = 1) {
        try {
            if (!canvasTrack) {
                console.warn('replaceVideoStream: No canvas track provided');
                return { status: 'no-track' };
            }

            if (!this._webcamProducer) {
                console.warn('replaceVideoStream: No webcam producer found');
                return { status: 'no-producer' };
            }

            await this._webcamProducer.replaceTrack({ track: canvasTrack });

            const producerData = {
                id: this._webcamProducer.id,
                kind: 'video',
                track: canvasTrack,
                paused: this._webcamProducer.paused,
                rtpParameters: this._webcamProducer.rtpParameters,
                codec: this._webcamProducer.rtpParameters.codecs[0].mimeType.split('/')[1],
            };

            this._dispatch(updateLiveStreamSettings({
                video: true,
                producers: {
                    ...this._store.getState().live.settings.producers,
                    [this._webcamProducer.id]: producerData,
                },
            }));

            return { status: 'success', producerId: this._webcamProducer.id };
        }
        catch (error) {
            console.error(`replaceVideoStream() failed (Attempt ${attempt}):`, error);

            if (attempt < 3) {
                const retryDelay = 500 * attempt;
                console.log(`Retrying replaceVideoStream in ${retryDelay}ms...`);
                setTimeout(() => this.replaceVideoStream(canvasTrack, attempt + 1), retryDelay);
            }

            return { status: 'error', error };
        }
    }


    async reinitializeMicrophone() {
        if (!this._micProducer) {
            console.warn('reinitializeMicrophone: No active microphone producer');
            return { status: 'no-producer' };
        }

        try {
            // Get current state to check for selected microphone device
            const state = this._store.getState();
            const selectedMicDevice = state.reducer?.selectedMicDevice;

            const constraints: any = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1
                }
            };

            if (selectedMicDevice && selectedMicDevice !== 'default') {
                constraints.audio.deviceId = { exact: selectedMicDevice };
            }

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            const newTrack = stream.getAudioTracks()[0];

            if (!newTrack) {
                throw new Error('No audio track found from selected device');
            }

            // Stop the old track if it exists
            if (this._micProducer.track) {
                this._micProducer.track.stop();
            }

            // Replace the track in the producer
            await this._micProducer.replaceTrack({ track: newTrack });

            // Update the audio track reference
            this._audioTrack = newTrack;

            console.log('Microphone reinitialized with new device');

            // Update Redux state with the new track
            this._dispatch(updateLiveStreamSettings({
                producers: {
                    ...this._store.getState().live.settings.producers,
                    [this._micProducer.id]: {
                        ...this._store.getState().live.settings.producers[this._micProducer.id],
                        track: newTrack
                    }
                }
            }));

            return { status: 'success' };
        } catch (error) {
            console.error('Failed to reinitialize microphone:', error);
            return { status: 'error', error };
        }
    }

    async switchMicrophone(deviceId: string) {
        if (!this._micProducer) {
            console.log('No active mic producer to switch');
            return { status: 'no-producer' };
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: { exact: deviceId },
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                    sampleRate: 48000
                }
            });

            const newTrack = stream.getAudioTracks()[0];

            if (!newTrack) {
                throw new Error('No audio track found from selected device');
            }

            // Stop the old track
            if (this._micProducer.track) {
                this._micProducer.track.stop();
            }

            // Replace the track
            await this._micProducer.replaceTrack({ track: newTrack });

            // Update the audio track reference
            this._audioTrack = newTrack;

            console.log('Microphone switched successfully');

            // Update Redux state
            this._dispatch(updateLiveStreamSettings({
                producers: {
                    ...this._store.getState().live.settings.producers,
                    [this._micProducer.id]: {
                        ...this._store.getState().live.settings.producers[this._micProducer.id],
                        track: newTrack
                    }
                }
            }));

            return { status: 'success' };
        } catch (error) {
            console.error('Failed to switch microphone:', error);
            return { status: 'error', error };
        }
    }

    async consumerPauseAllAudio() {
        const currentState = this._store.getState();
        const consumers = currentState.live.settings?.consumers || {};
        let audioConsumers = Object.values(consumers)?.filter(
            (c: any) => (c.track?.kind || c.kind) === "audio" && c.track?.readyState === "live"
        );
        audioConsumers.forEach((consumer: any) => {
            consumer.locallyPaused = true;
            if (consumer.track) {
                consumer.track.enabled = false;
            }
        });
        const updatedConsumers = { ...consumers };
        audioConsumers.forEach((consumer: any) => {
            updatedConsumers[consumer.id] = { ...consumer };
        });
        this._dispatch(updateLiveStreamSettings({ consumers: updatedConsumers }));
    }

    async consumerResumeAllAudio() {
        const currentState = this._store.getState();
        const consumers = currentState.live.settings?.consumers || {};
        let audioConsumers = Object.values(consumers)?.filter(
            (c: any) => (c.track?.kind || c.kind) === "audio" && c.track?.readyState === "live"
        );

        const updatedConsumers = { ...consumers };
        audioConsumers.forEach((consumer: any) => {
            // Mutate track for immediate effect (MediaStreamTrack is mutable)
            if (consumer.track) {
                consumer.track.enabled = true;
            }
            // Create new consumer object without mutating original
            updatedConsumers[consumer.id] = {
                ...consumer,
                locallyPaused: false
            };
        });
        this._dispatch(updateLiveStreamSettings({ consumers: updatedConsumers }));
    }

    // In your RoomClient class (add this method)
    async updateAudioByTable(tables, myEmail, trainerEmail) {
        const currentState = this._store.getState();
        const consumers = currentState.live.settings?.consumers || {};

        // Determine allowed peer IDs: same table + trainer
        let allowedPeerIds = [trainerEmail || ''];
        if (tables && myEmail) {
            for (const tableData of tables) {
                const tableEmails = tableData.seats.map((seat: any) => seat.participant_email);
                if (tableEmails.includes(myEmail)) {
                    allowedPeerIds = [...allowedPeerIds, ...tableEmails];
                    break; // Found my table
                }
            }
        }

        // Filter live audio consumers
        let audioConsumers = Object.values(consumers)?.filter(
            (c: any) => c.kind === "audio" && c.track?.readyState === "live"
        );

        // Prepare updates
        const updatedConsumers = { ...consumers };
        let hasChanges = false;

        audioConsumers.forEach((consumer: any) => {
            const isAllowed = allowedPeerIds.includes(consumer.peerId);
            const newPaused = !isAllowed;
            const newTrackEnabled = isAllowed;

            if (consumer.locallyPaused !== newPaused || consumer.track.enabled !== newTrackEnabled) {
                // Update consumer
                updatedConsumers[consumer.id] = {
                    ...consumer,
                    locallyPaused: newPaused,
                };
                if (consumer.track) {
                    consumer.track.enabled = newTrackEnabled; // Immediate effect
                }
                hasChanges = true;
            }
        });

        if (hasChanges) {
            // Dispatch to Redux
            this._dispatch(updateLiveStreamSettings({ consumers: updatedConsumers }));
        }
    }

    async consumerPauseByMuteStates(muteStates) {
        const currentState = this._store.getState();
        const consumers = currentState.live.settings?.consumers || {};
        const audioConsumers = Object.values(consumers)?.filter(
            (c: any) => (c.track?.kind || c.kind) === "audio" && c.track?.readyState === "live"
        );

        // Prepare updates
        const updatedConsumers = { ...consumers };
        let hasChanges = false;

        Object.entries(muteStates).forEach(([peerId, shouldMute]) => {
            audioConsumers.forEach((consumer: any) => {
                if (consumer.peerId === peerId) {
                    const newPaused = shouldMute;
                    const newTrackEnabled = !shouldMute;
                    // Update consumer
                    updatedConsumers[consumer.id] = {
                        ...consumer,
                        locallyPaused: newPaused,
                    };
                    if (consumer.track) {
                        consumer.track.enabled = newTrackEnabled; // Immediate effect
                    }
                    hasChanges = true;
                }
            });
        });
        console.log('hasChanges', hasChanges);

        if (hasChanges) {
            // Dispatch to Redux
            this._dispatch(updateLiveStreamSettings({ consumers: updatedConsumers }));
        }
    }

    async consumerResumeRoleplayersAudio(muteStates) {
        const currentState = this._store.getState();
        const consumers = currentState.live.settings?.consumers || {};
        const audioConsumers = Object.values(consumers)?.filter(
            (c: any) => (c.track?.kind || c.kind) === "audio" && c.track?.readyState === "live"
        );

        // Prepare updates
        const updatedConsumers = { ...consumers };
        let hasChanges = false;

        Object.entries(muteStates).forEach(([peerId, shouldMute]) => {
            audioConsumers.forEach((consumer: any) => {
                if (consumer.peerId === peerId) {
                    const newPaused = shouldMute;
                    const newTrackEnabled = !shouldMute;
                    // Update consumer
                    updatedConsumers[consumer.id] = {
                        ...consumer,
                        locallyPaused: newPaused,
                    };
                    if (consumer.track) {
                        consumer.track.enabled = newTrackEnabled; // Immediate effect
                    }
                    hasChanges = true;
                }
            });
        });
        console.log('hasChanges', hasChanges);

        if (hasChanges) {
            // Dispatch to Redux
            this._dispatch(updateLiveStreamSettings({ consumers: updatedConsumers }));
        }
    }

    async consumerMuteParticipantByGroup(group, trainerEmail) {
        const currentState = this._store.getState();
        const consumers = currentState.live.settings?.consumers || {};

        const audioConsumers = Object.values(consumers)?.filter(
            (c: any) => (c.track?.kind || c.kind) === "audio" && c.track?.readyState === "live"
        );

        // Prepare updates
        const updatedConsumers = { ...consumers };
        let hasChanges = false;

        // Get all emails in the current group
        const groupEmails = group?.seats?.map((s: any) => s.participant_email) || [];

        audioConsumers.forEach((consumer: any) => {
            const peerId = consumer?.peerId;
            if (!peerId) return;

            let newPaused = true;
            let newTrackEnabled = false;

            // 🧑‍🏫 Trainer is always unmuted
            if (peerId === trainerEmail) {
                newPaused = false;
                newTrackEnabled = true;
            }
            // 🎭 If the consumer belongs to the same group as current user → unmute
            else if (groupEmails.includes(peerId)) {
                newPaused = false;
                newTrackEnabled = true;
            }

            // Update consumer state
            if (
                consumer.locallyPaused !== newPaused ||
                consumer.track?.enabled !== newTrackEnabled
            ) {
                updatedConsumers[consumer.id] = {
                    ...consumer,
                    locallyPaused: newPaused,
                };

                if (consumer.track) {
                    consumer.track.enabled = newTrackEnabled; // Immediate effect
                }

                hasChanges = true;
            }
        });
        if (hasChanges) {
            this._dispatch(updateLiveStreamSettings({ consumers: updatedConsumers }));
        }
    }

    getConsumerPeers(consumers: any, newConsumer: any) {
        const peerList = Object.values(consumers);
        const consumerIds = peerList .filter((a: any) => a?.kind === "audio")
        .map((a: any) => a.peerId); // remove undefined/null

        // Ensure unique
        const uniqueIds = Array.from(new Set(consumerIds));

        // Add the new peerId if not already present
        if (newConsumer?.peerId && !uniqueIds.includes(newConsumer.peerId)) {
            uniqueIds.push(newConsumer.peerId);
        }
        return uniqueIds;
    }


}